import { TimeEntry } from "../store/timeTrackerStore";

// Read-only "when did I work?" projection. Pure functions, no client identity.
// Two layers compose to drive the WorkRhythmPanel:
//   1. Split each TimeEntry into one or more per-logical-day intervals,
//      keyed by the logical-day-start epoch ms (see DAY_CUTOFF_HOUR below).
//   2. On top of that, smooth (close short gaps), filter to qualifying days,
//      and aggregate across days into a fraction-of-days-active vector for the
//      "typical day" column.
//
// Two smoothing layers coexist and are deliberately decoupled:
//   * VISUAL (DAY_COLUMN_GAP_MS, TYPICAL_INTERNAL_GAP_MS): hardcoded,
//     panel-only, used only when painting the calendar columns. Drives no
//     number the user reads.
//   * REPORTING (settings.smoothing in the store): user-tunable, drives the
//     numbers in the panel's Today/Typical summary cards AND the rest of the
//     app's analytics + billing. The rhythm panel just reads this — it never
//     redefines smoothing for reporting.
//
// "Day" semantics, panel-only:
//   The work-day is anchored at DAY_CUTOFF_HOUR (04:00 local), NOT at
//   calendar midnight. Work between 00:00 and 04:00 attributes to the
//   previous calendar day's logical day. This is what stops a 23:30→01:30
//   late session, or a fresh 01:00 entry after a midnight break, from
//   ghost-starting the next day at 00:00.
//
//   This anchoring lives ONLY inside this file (and the panel that consumes
//   it). Billing, pacing, the entries list, and Settings → Smoothing all
//   keep calendar-midnight semantics — do not lift logicalDayStart into
//   their code paths.
//
// All "minute-of-day" values are offsets from the logical-day start, in
// milliseconds. DST flips are handled by always constructing the next/prev
// day via `new Date(y, m, d±1, DAY_CUTOFF_HOUR)` rather than `±24h`.

export interface DayInterval {
  // ms since the logical-day start (DAY_CUTOFF_HOUR) of the day this
  // interval belongs to
  startMs: number;
  endMs: number;
}

const HOUR_MS = 60 * 60 * 1000;
const MIN_MS = 60 * 1000;
export const DAY_MS = 24 * HOUR_MS;

// Wall-clock hour at which the logical work-day rolls over. Picked at 04:00
// because the user's typical "done by" is ~02:00 and earliest start is
// ~06:00 — 04:00 sits cleanly in the middle. Industry-standard for time
// trackers (Toggl, Harvest use the same cutoff).
export const DAY_CUTOFF_HOUR = 4;

// Logical-day-start epoch ms for the logical day owning `ms`. Work between
// 00:00 and DAY_CUTOFF_HOUR is attributed to the previous calendar day.
export const logicalDayStart = (ms: number): number => {
  const d = new Date(ms);
  const dayOffset = d.getHours() < DAY_CUTOFF_HOUR ? -1 : 0;
  return new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate() + dayOffset,
    DAY_CUTOFF_HOUR,
  ).getTime();
};

// Logical-day-start epoch ms for the logical day immediately after the one
// owning `ms`. Goes via `new Date(y, m, d+1, DAY_CUTOFF_HOUR)` rather than
// +DAY_MS so spring-forward / fall-back transitions land on the right
// wall-clock cutoff.
const logicalNextDayStart = (ms: number): number => {
  const start = new Date(logicalDayStart(ms));
  return new Date(
    start.getFullYear(),
    start.getMonth(),
    start.getDate() + 1,
    DAY_CUTOFF_HOUR,
  ).getTime();
};

// Splits a single time entry into per-logical-day intervals. A running entry
// (endTime undefined) is clamped to `now`. Entries with end <= start are
// dropped. The output is sorted by day. An entry that spans a 04:00 cutoff
// (e.g. 03:30→05:00) splits at that cutoff; an entry that crosses calendar
// midnight without crossing 04:00 (e.g. 23:30→01:30) stays whole, attributed
// to the day it started.
function splitEntryAcrossDays(
  entry: TimeEntry,
  now: number,
): Array<{ dayStartMs: number; interval: DayInterval }> {
  const end = entry.endTime ?? now;
  if (end <= entry.startTime) return [];

  const out: Array<{ dayStartMs: number; interval: DayInterval }> = [];
  let cursor = entry.startTime;
  while (cursor < end) {
    const dayStart = logicalDayStart(cursor);
    const nextDay = logicalNextDayStart(cursor);
    const segEnd = Math.min(end, nextDay);
    out.push({
      dayStartMs: dayStart,
      interval: { startMs: cursor - dayStart, endMs: segEnd - dayStart },
    });
    cursor = segEnd;
  }
  return out;
}

// Merge a list of intervals into a sorted, non-overlapping set. Intervals
// touching (a.endMs === b.startMs) are coalesced.
export function mergeIntervals(intervals: DayInterval[]): DayInterval[] {
  if (intervals.length <= 1) return intervals.slice();
  const sorted = [...intervals].sort((a, b) => a.startMs - b.startMs);
  const out: DayInterval[] = [];
  let cur: DayInterval = { ...sorted[0] };
  for (let i = 1; i < sorted.length; i++) {
    const next = sorted[i];
    if (next.startMs <= cur.endMs) {
      cur.endMs = Math.max(cur.endMs, next.endMs);
    } else {
      out.push(cur);
      cur = { ...next };
    }
  }
  out.push(cur);
  return out;
}

// Close gaps shorter than or equal to thresholdMs. Input must be sorted and
// non-overlapping (run mergeIntervals first if unsure). thresholdMs <= 0 is a
// no-op.
export function closeGaps(
  intervals: DayInterval[],
  thresholdMs: number,
): DayInterval[] {
  if (thresholdMs <= 0 || intervals.length <= 1) return intervals.slice();
  const out: DayInterval[] = [];
  let cur: DayInterval = { ...intervals[0] };
  for (let i = 1; i < intervals.length; i++) {
    const next = intervals[i];
    const gap = next.startMs - cur.endMs;
    if (gap <= thresholdMs) {
      cur.endMs = Math.max(cur.endMs, next.endMs);
    } else {
      out.push(cur);
      cur = { ...next };
    }
  }
  out.push(cur);
  return out;
}

// Total worked ms for a sorted, non-overlapping interval list.
export function totalWorkMs(intervals: DayInterval[]): number {
  let sum = 0;
  for (const i of intervals) sum += i.endMs - i.startMs;
  return sum;
}

// Group entries by logical day into merged interval lists. Each day's
// intervals are sorted and non-overlapping. Running entries are clamped to
// `now`. Entries spanning a 04:00 cutoff are split at that boundary.
export function entriesByLocalDay(
  entries: TimeEntry[],
  now: number,
): Map<number, DayInterval[]> {
  const buckets = new Map<number, DayInterval[]>();
  for (const entry of entries) {
    for (const { dayStartMs, interval } of splitEntryAcrossDays(entry, now)) {
      const list = buckets.get(dayStartMs);
      if (list) list.push(interval);
      else buckets.set(dayStartMs, [interval]);
    }
  }
  const out = new Map<number, DayInterval[]>();
  for (const [day, ivs] of buckets) out.set(day, mergeIntervals(ivs));
  return out;
}

// Pick the most recent `count` days at-or-before `refDayStart` that satisfy
// `predicate`. Walks back at most `maxLookbackDays` calendar days so a
// near-empty store doesn't drive an unbounded loop. Newest first. The
// walk-back preserves `refDayStart`'s wall-clock time-of-day, so a caller
// passing a logicalDayStart (at 04:00) gets a sequence of 04:00-anchored
// keys; a caller passing a midnight-anchored key gets midnight-anchored
// keys. DST-safe via the calendar-day construction.
export function pickRecentDays(
  byDay: Map<number, DayInterval[]>,
  refDayStart: number,
  count: number,
  predicate: (intervals: DayInterval[]) => boolean,
  maxLookbackDays = 120,
): number[] {
  const out: number[] = [];
  let cursor = refDayStart;
  for (let i = 0; i < maxLookbackDays && out.length < count; i++) {
    const ivs = byDay.get(cursor);
    if (ivs && predicate(ivs)) out.push(cursor);
    const d = new Date(cursor);
    cursor = new Date(
      d.getFullYear(),
      d.getMonth(),
      d.getDate() - 1,
      d.getHours(),
      d.getMinutes(),
      d.getSeconds(),
      d.getMilliseconds(),
    ).getTime();
  }
  return out;
}

// Auto-fit the visible hour range to the data. Returns [fromHour, toHour] as
// integer hour boundaries, rounded outward, with at least `minSpanHours`
// hours of span. Hours are offsets-from-logical-day-start, so 0 = 04:00
// wall-clock and 24 = 04:00 the next day. The default fallback [5, 17]
// corresponds to wall-clock 09:00–21:00. If no intervals fall in range,
// returns `fallback`.
export function fitHourRange(
  days: DayInterval[][],
  fallback: [number, number] = [5, 17],
  minSpanHours = 6,
): [number, number] {
  let earliest = Infinity;
  let latest = -Infinity;
  for (const ivs of days) {
    for (const iv of ivs) {
      if (iv.startMs < earliest) earliest = iv.startMs;
      if (iv.endMs > latest) latest = iv.endMs;
    }
  }
  if (!Number.isFinite(earliest) || !Number.isFinite(latest)) return fallback;

  let fromHour = Math.floor(earliest / HOUR_MS);
  let toHour = Math.ceil(latest / HOUR_MS);
  // Snap into [0, 24]. An interval ending exactly at 24:00 is fine.
  if (fromHour < 0) fromHour = 0;
  if (toHour > 24) toHour = 24;

  if (toHour - fromHour < minSpanHours) {
    const need = minSpanHours - (toHour - fromHour);
    const padBefore = Math.min(fromHour, Math.floor(need / 2));
    const padAfter = Math.min(24 - toHour, need - padBefore);
    fromHour -= padBefore;
    toHour += padAfter;
  }
  return [fromHour, toHour];
}

// Sum-of-intervals over the visible window. Useful for the per-column total.
export function workMsInRange(
  intervals: DayInterval[],
  fromMs: number,
  toMs: number,
): number {
  let sum = 0;
  for (const iv of intervals) {
    const s = Math.max(iv.startMs, fromMs);
    const e = Math.min(iv.endMs, toMs);
    if (e > s) sum += e - s;
  }
  return sum;
}

export interface DaySummary {
  firstStartMs: number | null; // ms since the logical-day start, of the first interval
  lastEndMs: number | null;
  totalWorkMs: number;
  // Inside-day break time = (lastEnd - firstStart) - totalWork. Excludes
  // pre-first and post-last "breaks" which would otherwise dwarf actual breaks.
  insideBreakMs: number;
  sessionCount: number;
}

export function summarizeDay(intervals: DayInterval[]): DaySummary {
  if (intervals.length === 0) {
    return {
      firstStartMs: null,
      lastEndMs: null,
      totalWorkMs: 0,
      insideBreakMs: 0,
      sessionCount: 0,
    };
  }
  const firstStartMs = intervals[0].startMs;
  const lastEndMs = intervals[intervals.length - 1].endMs;
  const total = totalWorkMs(intervals);
  return {
    firstStartMs,
    lastEndMs,
    totalWorkMs: total,
    insideBreakMs: Math.max(0, lastEndMs - firstStartMs - total),
    sessionCount: intervals.length,
  };
}

// For the typical column. Bucket the visible window into `bucketMs`-wide
// slots and, for each slot, compute the fraction of qualifying days that
// were active for any part of that slot. `daysIntervals` must already be
// per-day smoothed (typically with a bigger gap-close threshold than the
// user's display setting). The returned array has length
// `(toMs - fromMs) / bucketMs` (rounded down).
export function fractionOfDaysActive(
  daysIntervals: DayInterval[][],
  fromMs: number,
  toMs: number,
  bucketMs: number,
): number[] {
  const bucketCount = Math.max(0, Math.floor((toMs - fromMs) / bucketMs));
  if (bucketCount === 0 || daysIntervals.length === 0) {
    return new Array(bucketCount).fill(0);
  }
  // For each day, mark its active buckets, then sum across days.
  const counts = new Array<number>(bucketCount).fill(0);
  const marked = new Uint8Array(bucketCount);
  for (const ivs of daysIntervals) {
    marked.fill(0);
    for (const iv of ivs) {
      const s = Math.max(iv.startMs, fromMs);
      const e = Math.min(iv.endMs, toMs);
      if (e <= s) continue;
      const firstBucket = Math.floor((s - fromMs) / bucketMs);
      // A bucket is "active" if any part of it overlaps the interval. So the
      // last bucket is the one containing (e - epsilon). Using ceil(...) - 1.
      const lastBucket = Math.min(
        bucketCount - 1,
        Math.ceil((e - fromMs) / bucketMs) - 1,
      );
      for (let i = firstBucket; i <= lastBucket; i++) marked[i] = 1;
    }
    for (let i = 0; i < bucketCount; i++) counts[i] += marked[i];
  }
  const n = daysIntervals.length;
  return counts.map((c) => c / n);
}

// Format an inside-day offset (ms since the logical-day start) as wall-clock
// HH:MM in 24h. An offset of 21.5h with a 04:00 cutoff renders as "01:30",
// not "21:30" — the cutoff frame is invisible to the reader.
export function formatTimeOfDay(msSinceDayStart: number): string {
  const wallMs =
    (DAY_CUTOFF_HOUR * HOUR_MS + msSinceDayStart) % DAY_MS;
  const totalMin = Math.floor(wallMs / MIN_MS);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
}

// Format a duration like "7h42m" / "38m" / "9h". Deliberately *not*
// `formatDuration({short:true})` from TimeTrackerUtils — that one joins
// units with a space ("7h 42m"), which is one character too wide for the
// 72px column headers in this panel. Single purpose: fit a workday total
// into a tight column sublabel.
export function formatDurationCompact(ms: number): string {
  if (ms < MIN_MS) return "0m";
  const totalMin = Math.round(ms / MIN_MS);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h${m.toString().padStart(2, "0")}m`;
}

// VISUAL smoothing for the day-column shapes. Hardcoded — independent of
// the global smoothing setting because the rhythm view is purely visual
// here: real entries are fragmented enough that anything under ~15 min
// shreds the day's shape, and a genuine coffee/lunch break is much longer.
// Drives ONLY the painted columns; the Today summary card uses the global
// setting (see WorkRhythmPanel).
export const DAY_COLUMN_GAP_MS = 15 * MIN_MS;

// VISUAL smoothing for the typical column. Hardcoded, larger than
// DAY_COLUMN_GAP_MS — the typical column reads as "I usually work 9–12 then
// 14–18", and the recurring-pattern lens should bridge breaks too short to
// repeat across days while keeping the real long breaks visible as gaps.
// Drives ONLY the painted column; the Typical summary card uses the global
// setting.
export const TYPICAL_INTERNAL_GAP_MS = 30 * MIN_MS;

// Minimum day-active fraction below which a bucket is considered noise in the
// typical column. Below this, the bucket renders blank rather than as a faint
// stripe — keeps the column from looking like fog on scattered data.
export const TYPICAL_NOISE_FLOOR = 0.25;

// Bucket size for the typical column. Coarser than 1 minute so small jitter in
// start times doesn't fragment the column visually; fine enough to resolve a
// lunch break.
export const TYPICAL_BUCKET_MS = 5 * MIN_MS;
